import React, { useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../lib/api";
import { useScope } from "../context/ScopeContext";
import { useI18n } from "../i18n";
import { useAuth } from "../context/AuthContext";
import { localizeName } from "../lib/catI18n";
import { PageTitle, Spinner, EmptyState, OverviewByScope } from "../lib/ui";
import { money } from "../lib/format";
import Modal from "../components/Modal";
import { BarChart3 } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, LineChart, Line, CartesianGrid, ReferenceLine, LabelList } from "recharts";

// Paleta con contraste suficiente, sin tonos casi iguales lado a lado.
const CAT_PALETTE = ["#0299A0", "#E0803A", "#075E62", "#C44E6B", "#4A7FB5", "#8BA83E", "#9A6FB0", "#C9A66B"];
const OTHERS_COLOR = "#9AA6A2";
const AXIS = "#7E8A86";
const tipStyle = { background: "var(--surface)", border: "1px solid var(--line)", borderRadius: 8, fontSize: 12, color: "var(--ink)" };

function noNbsp(s) { return s.replace(/\u00A0/g, " "); }

function ChartCard({ title, subtitle, action, testid, children }) {
  return (
    <div className="card" data-testid={testid} style={{ display: "flex", flexDirection: "column" }}>
      <div className="card-h" style={{ alignItems: "flex-start" }}>
        <div style={{ minWidth: 0 }}>
          <h2>{title}</h2>
          {subtitle && <p className="ov-sub">{subtitle}</p>}
        </div>
        {action}
      </div>
      <div className="card-b" style={{ flex: 1 }}>{children}</div>
    </div>
  );
}

function PieTip({ active, payload, total }) {
  if (!active || !payload || !payload.length) return null;
  const d = payload[0];
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0";
  return (
    <div className="ov-tip-box">
      <div style={{ fontWeight: 600 }}>{d.name}</div>
      <div className="tv">{noNbsp(money(d.value))} · {pct}%</div>
    </div>
  );
}

function LegendRow({ d, i, color, total, testid }) {
  const pct = total > 0 ? ((d.value / total) * 100).toFixed(1) : "0";
  return (
    <div className="ov-legrow" data-testid={testid}>
      <span className="ov-legdot" style={{ background: color }} />
      <span className="ov-legname">{d.name}</span>
      <span className="ov-legval num">{noNbsp(money(d.value))}</span>
      <span className="ov-legpct">{pct}%</span>
    </div>
  );
}

// Donut + lista ordenada (color, nombre, valor, %). Agrupa el resto en "Otros" y permite "Ver todas".
function DonutWithList({ title, subtitle, items, colors, maxSlices = 6, testid }) {
  const { t } = useI18n();
  const [showAll, setShowAll] = useState(false);
  const total = items.reduce((s, d) => s + d.value, 0);
  const top = items.slice(0, maxSlices);
  const rest = items.slice(maxSlices);
  const othersVal = rest.reduce((s, d) => s + d.value, 0);
  const slices = othersVal > 0 ? [...top, { name: t("charts.others"), value: othersVal, _o: true }] : top;
  const colAt = (i, o) => (o ? OTHERS_COLOR : colors[i % colors.length]);
  const action = rest.length > 0
    ? <button className="ov-seeall" data-testid={`see-all-${testid}`} onClick={() => setShowAll(true)}>{t("charts.seeAll")}</button>
    : null;
  return (
    <ChartCard title={title} subtitle={subtitle} action={action} testid={`card-${testid}`}>
      <div className="ov-donut-wrap">
        <div className="ov-donut">
          <ResponsiveContainer width="100%" height={210}>
            <PieChart>
              <Pie data={slices} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={85} innerRadius={52} paddingAngle={1}>
                {slices.map((d, i) => <Cell key={i} fill={colAt(i, d._o)} stroke="var(--surface)" strokeWidth={2} />)}
              </Pie>
              <Tooltip content={<PieTip total={total} />} />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <div className="ov-leglist" data-testid={`legend-${testid}`}>
          {slices.map((d, i) => <LegendRow key={i} d={d} i={i} color={colAt(i, d._o)} total={total} testid={`legend-${testid}-${i}`} />)}
        </div>
      </div>
      <Modal open={showAll} onClose={() => setShowAll(false)} title={t("charts.allCategories")}>
        <div className="ov-leglist ov-leglist-modal" data-testid={`all-list-${testid}`}>
          {items.map((d, i) => <LegendRow key={i} d={d} i={i} color={colors[i % colors.length]} total={total} testid={`all-${testid}-${i}`} />)}
        </div>
      </Modal>
    </ChartCard>
  );
}

export default function Charts() {
  const { scope } = useScope();
  const { locale, t, localeTag } = useI18n();
  const { user } = useAuth();
  const isDemo = user?.is_demo_session;
  const tr = (arr) => (arr || []).map((d) => ({ ...d, name: localizeName(d.name, locale) }));
  const [range, setRange] = useState({ date_from: "", date_to: "" });
  const [data, setData] = useState(null);
  const [ov, setOv] = useState(null);
  const [plScope, setPlScope] = useState(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(false);
  const CE = ({
    es: { msg: "No se pudieron cargar los gráficos.", retry: "Reintentar" },
    en: { msg: "Charts could not be loaded.", retry: "Retry" },
    fr: { msg: "Impossible de charger les graphiques.", retry: "Réessayer" },
    pt: { msg: "Não foi possível carregar os gráficos.", retry: "Tentar novamente" },
    it: { msg: "Impossibile caricare i grafici.", retry: "Riprova" },
  })[locale] || { msg: "No se pudieron cargar los gráficos.", retry: "Reintentar" };

  const load = useCallback(async () => {
    setLoading(true); setErr(false);
    try {
      const params = {};
      if (scope !== "all") params.scope = scope;
      if (range.date_from) params.date_from = range.date_from;
      if (range.date_to) params.date_to = range.date_to;
      const reqs = [api.get("/metrics/charts", { params })];
      if (scope === "all") {
        reqs.push(api.get("/metrics/summary", { params: { ...params, scope: "personal" } }));
        reqs.push(api.get("/metrics/summary", { params: { ...params, scope: "professional" } }));
        reqs.push(api.get("/metrics/charts", { params: { ...params, scope: "personal" } }));
        reqs.push(api.get("/metrics/charts", { params: { ...params, scope: "professional" } }));
      }
      const res = await Promise.all(reqs);
      setData(res[0].data);
      if (scope === "all") {
        setOv({ personal: res[1].data, pro: res[2].data });
        setPlScope({ personal: res[3].data.profit_loss, professional: res[4].data.profit_loss });
      } else { setOv(null); setPlScope(null); }
    } catch (e) {
      setErr(true);
    } finally {
      setLoading(false);
    }
  }, [scope, range]);

  useEffect(() => { load(); }, [load]);

  // Resultado mensual: en Visión general => Personal/Profesional/Total; en un ámbito => Ingresos/Gastos/Resultado.
  const monthly = useMemo(() => {
    if (scope === "all" && plScope) {
      const map = {};
      (plScope.personal || []).forEach((p) => { map[p.period] = map[p.period] || { period: p.period, personal: 0, professional: 0 }; map[p.period].personal = p.result; });
      (plScope.professional || []).forEach((p) => { map[p.period] = map[p.period] || { period: p.period, personal: 0, professional: 0 }; map[p.period].professional = p.result; });
      return Object.values(map).sort((a, b) => a.period.localeCompare(b.period)).map((m) => ({ ...m, total: Number((m.personal + m.professional).toFixed(2)) }));
    }
    return data?.profit_loss || [];
  }, [scope, plScope, data]);

  const fmtK = (v) => new Intl.NumberFormat(localeTag, { maximumFractionDigits: 0, useGrouping: true, minimumGroupingDigits: 1 }).format(Math.round(v));
  const hasData = data && (data.expense_by_category.length || data.income_by_service.length || data.profit_loss.length);

  // Ingresos por servicio: 8 mayores + "Otros".
  const incomeData = useMemo(() => {
    if (!data) return [];
    const list = tr(data.income_by_service);
    const top = list.slice(0, 8);
    const othersVal = list.slice(8).reduce((s, d) => s + d.value, 0);
    return othersVal > 0 ? [...top, { name: t("charts.others"), value: othersVal }] : top;
  }, [data, locale]);

  const fixedVar = useMemo(() => (data?.fixed_vs_variable || []).map((d) => ({ ...d, name: d.name === "Fijos" ? t("chart.fixed") : t("chart.variable") })), [data, locale]);

  const monthlyLines = scope === "all"
    ? [
        { key: "personal", color: "#35BBAE", label: t("scope.personal"), dash: null },
        { key: "professional", color: "#075E62", label: t("scope.professional"), dash: null },
        { key: "total", color: "#8E6F3E", label: t("ov.total"), dash: null },
      ]
    : [
        { key: "income", color: "#0299A0", label: t("chart.income"), dash: null },
        { key: "expense", color: "#E0803A", label: t("chart.expense"), dash: null },
        { key: "result", color: "#8E6F3E", label: t("chart.result"), dash: "5 4" },
      ];

  return (
    <div className="relative">
      <PageTitle title={t("p.charts.t")} subtitle={t("p.charts.s")}>
        <div className="flex items-center gap-2">
          <input type="date" value={range.date_from} onChange={(e) => setRange({ ...range, date_from: e.target.value })} className="h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm" data-testid="charts-from" />
          <span className="text-[#5F8588] text-sm">→</span>
          <input type="date" value={range.date_to} onChange={(e) => setRange({ ...range, date_to: e.target.value })} className="h-10 px-3 rounded-md border border-[#D5E7E5] bg-white text-sm" data-testid="charts-to" />
        </div>
      </PageTitle>

      {isDemo && (
        <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center overflow-hidden" aria-hidden="true">
          <span className="text-[7rem] sm:text-[9rem] font-black text-[#075E62]/[0.06] -rotate-12 select-none tracking-widest">DEMO</span>
        </div>
      )}

      {scope === "all" && (
        <p className="text-sm text-[#5F8588] mb-4 max-w-2xl" data-testid="charts-overview-note">{t("ov.explain")}</p>
      )}

      {scope === "all" && ov && (
        <div className="card mb-6">
          <div className="card-h"><h2>{t("ov.byScope")}</h2></div>
          <div className="card-b"><OverviewByScope personal={ov.personal} pro={ov.pro} /></div>
        </div>
      )}

      {loading ? <Spinner /> : err ? (
        <div className="bg-white border border-[#D5E7E5] rounded-lg p-10 text-center" data-testid="charts-error">
          <p className="text-sm text-[#B4533F] mb-4">{CE.msg}</p>
          <button onClick={load} data-testid="charts-retry" className="text-sm px-4 py-2 rounded bg-[#0299A0] text-white hover:bg-[#077c82]">{CE.retry}</button>
        </div>
      ) : !hasData ? (
        <div className="bg-white border border-[#D5E7E5] rounded-lg"><EmptyState icon={BarChart3} title={t("charts.noData")} text={t("charts.noDataText")} /></div>
      ) : (
        <div className="grid lg:grid-cols-2 gap-6">
          <DonutWithList title={t("charts.expenseByCategory")} subtitle={t("charts.sub.expense")} items={tr(data.expense_by_category)} colors={CAT_PALETTE} maxSlices={6} testid="expense" />

          <ChartCard title={t("charts.incomeByService")} subtitle={t("charts.sub.income")} testid="card-income">
            <ResponsiveContainer width="100%" height={Math.max(220, incomeData.length * 42)}>
              <BarChart data={incomeData} layout="vertical" margin={{ left: 8, right: 60, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={AXIS} strokeOpacity={0.18} horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 10.5, fill: AXIS }} tickFormatter={fmtK} />
                <YAxis type="category" dataKey="name" width={146} tick={{ fontSize: 10.5, fill: AXIS }} interval={0} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => noNbsp(money(v))} cursor={{ fill: AXIS, opacity: 0.08 }} />
                <Bar dataKey="value" fill="#0299A0" radius={[0, 4, 4, 0]} maxBarSize={26}>
                  <LabelList dataKey="value" position="right" formatter={fmtK} style={{ fontSize: 10, fill: AXIS }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>

          <DonutWithList title={t("charts.fixedVsVariable")} subtitle={t("charts.sub.fixed")} items={fixedVar} colors={["#075E62", "#E0803A"]} maxSlices={2} testid="fixed" />

          <ChartCard title={t("charts.monthlyResult")} subtitle={t("charts.sub.monthly")} testid="card-monthly">
            <div className="ov-chart-legend" data-testid="monthly-legend">
              {monthlyLines.map((l) => (
                <span className="it" key={l.key}><span className="ln" style={{ background: l.color, ...(l.dash ? { backgroundImage: `repeating-linear-gradient(90deg, ${l.color} 0 5px, transparent 5px 9px)`, background: "transparent" } : {}) }} />{l.label}</span>
              ))}
            </div>
            <ResponsiveContainer width="100%" height={260}>
              <LineChart data={monthly} margin={{ left: 4, right: 14, top: 4, bottom: 4 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={AXIS} strokeOpacity={0.18} />
                <XAxis dataKey="period" tick={{ fontSize: 10.5, fill: AXIS }} />
                <YAxis tick={{ fontSize: 10.5, fill: AXIS }} tickFormatter={fmtK} width={58} />
                <ReferenceLine y={0} stroke={AXIS} strokeOpacity={0.55} />
                <Tooltip contentStyle={tipStyle} formatter={(v) => noNbsp(money(v))} />
                {monthlyLines.map((l) => (
                  <Line key={l.key} type="linear" dataKey={l.key} name={l.label} stroke={l.color} strokeWidth={2} dot={false} strokeDasharray={l.dash || undefined} />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </ChartCard>
        </div>
      )}
    </div>
  );
}
